import React from 'react';
import { Box, Typography } from '@mui/material';
import { getRayo, colorRayo, colorIdentidad } from '../config/rayos';
import { IMAGENES_VOZ } from '../config/imagenes';
import { imagenDelDia } from '../config/imagenesDeepArt';
import { useTranslation } from 'react-i18next';
import { nombreVoz } from '../config/nombres_voz';

/**
 * LA LUZ DE LA VOZ — la cabecera de una canalizacion.
 *
 * Si la entidad ya tiene imagen propia, se muestra la imagen (siempre generada
 * en el color de su rayo). Mientras no la tenga, se dibuja su luz: un resplandor
 * del color del rayo que baja desde el borde superior. No es un relleno
 * provisional feo: es el mismo sistema, sin fotografia.
 *
 * 🖼️ PLAN_IMAGENES.md §0 (23-sep): "al abrir una canalizacion especifica, la
 * imagen de su entidad va arriba del titulo". La elige `imagenDelDia` (ver
 * imagenesDeepArt.js): la misma todo el dia, otra al dia siguiente.
 * `pieza` es opcional a proposito: si no llega (por ejemplo, una vista que
 * solo tiene el nombre de la entidad), se cae al comportamiento de siempre.
 */
const LuzDeLaVoz = ({ entidad, modo = 'oscuro', alto = '13rem', pieza = null }) => {
  const { t } = useTranslation();
  const rayo = colorRayo(entidad, modo);        // para el nombre de la voz, que es texto
  const luz = colorIdentidad(entidad, modo);    // para el resplandor, que no lleva letras
  const datos = getRayo(entidad);
  const imagenDeepArt = imagenDelDia(pieza);
  const imagen = imagenDeepArt || IMAGENES_VOZ[entidad];

  /* Los retratos de Deep Art no comparten proporción: las 4 entidades con
     retrato individual (Jeshua, María, María Magdalena, Los Esenios) son
     cuadradas (800×800), pero Trabajadores de la Luz es una escena de grupo
     panorámica (800×446) -el mismo motivo por el que sus fotos ya se
     resizaron a su proporción real y no se estiraron el 23-sep-. Meterlas en
     una franja panorámica fija (banner ancho y bajo) o en un marco cuadrado
     fijo recorta una de las dos. La solución que no recorta NINGUNA: el
     `<img>` lleva `width/height: auto` con un `max-width`/`max-height`, así
     el navegador la achica respetando SU proporción real -sin cortar nada,
     sin estirar, sin relleno-, y el marco (fondo, sombra, esquinas) se ajusta
     solo a lo que la foto realmente ocupa. Las voces sin Deep Art (imagen =
     null o `IMAGENES_VOZ`, hoy vacío) siguen con la franja panorámica de
     siempre, sin cambios.

     El velo de arriba/abajo (que sí tenía la franja panorámica vieja, y se
     había perdido al pasar a este marco) vuelve como una capa ENCIMA de la
     foto -nunca recorta, es solo un degradado hacia `--fondo` en los bordes
     superior e inferior, para que la foto se funda con la página en vez de
     cortar en seco contra el nombre de la voz. */

  return (
    <Box
      className="luz-de-la-voz"
      sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      {imagenDeepArt ? (
        <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: 'min(94vw, 640px)', maxHeight: '24rem' }}>
          <Box
            component="img"
            src={imagenDeepArt}
            alt={nombreVoz(entidad, t)}
            sx={{
              display: 'block',
              width: 'auto', height: 'auto',
              maxWidth: 'min(94vw, 640px)',
              maxHeight: '24rem',
              borderRadius: '14px',
              boxShadow: `0 0 0 1px ${rayo}40, 0 16px 48px rgba(0,0,0,.4)`
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute', inset: 0, borderRadius: '14px', pointerEvents: 'none',
              background: 'linear-gradient(to bottom, var(--fondo) 0%, transparent 16%, transparent 78%, var(--fondo) 100%)'
            }}
          />
        </Box>
      ) : (
        <Box sx={{
          position: 'relative', height: alto, width: '100%', overflow: 'hidden',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
        }}>
          {imagen ? (
            <>
              <Box
                component="img"
                src={imagen}
                alt={nombreVoz(entidad, t)}
                sx={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  objectFit: 'cover', objectPosition: 'center center'
                }}
              />
              <Box sx={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,.15) 0%, var(--fondo) 92%)'
              }} />
            </>
          ) : (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute', inset: 0,
                background: `
                  radial-gradient(60% 90% at 50% -18%, ${luz}59 0%, ${luz}1F 42%, transparent 72%),
                  radial-gradient(22% 40% at 50% -4%, ${luz}8C 0%, transparent 70%)
                `
              }}
            />
          )}
        </Box>
      )}

      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5, pt: imagenDeepArt ? 2 : 0, pb: 1 }}>
        <Box sx={{ height: '1px', width: '2.5rem', bgcolor: rayo, opacity: .5 }} />
        <Typography component="p" sx={{
          fontFamily: 'var(--fuente-titulo)', fontSize: '.78rem', letterSpacing: '.2em',
          textTransform: 'uppercase', color: rayo
        }}>
          {/* el nombre que VE el lector va en su idioma (nombres_voz); la clave sigue en español */}
          {nombreVoz(entidad, t)}
        </Typography>
        <Box sx={{ height: '1px', width: '2.5rem', bgcolor: rayo, opacity: .5 }} />
      </Box>
    </Box>
  );
};

export default LuzDeLaVoz;
